import React, { forwardRef, useImperativeHandle, useEffect } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import cn from 'classnames';
import find from 'lodash/find';
import { Checkbox } from 'antd';
import { useModal } from 'components';
import {
  scrollToErrorField,
  validateForm,
  standardRequiredFieldObj,
} from 'utils/form';
import { FormItem } from 'components/Form';
import { getCurrencyValue } from 'utils/number';
import InputItem from 'views/pages/ClientDetail/components/InputItem';
import { FormSection } from 'views/pages/ClientDetail/components/Elements';
import { useNewOrderModalData, useActions } from '../../../../selectorData';
import { orderTypeValidateObj } from '../../utils';
import { ORDER_FLOW_TYPES } from '../../constants';
import classes from '../../NewOrderModal.module.scss';
import styles from './OrderType.module.scss';

const packageValidateObj = {
  package_id: [standardRequiredFieldObj],
  requested_by: [standardRequiredFieldObj],
};

const OrderTypeStep = forwardRef(
  (
    {
      form,
      style,
      setStepDescription,
      orderTypeId,
      setOrderTypeId,
      setFormDetailsFieldPopulated,
      setAddItems,
      isEditOrder,
      orderFlowType,
      setOrderFlowType,
      cartOrders = [],
      selectedPackage,
      setSelectedPackage,
      packageOrderIndex,
      setPackageOrderIndex,
    },
    ref
  ) => {
    const {
      orderTypes,
      orderRequesters,
      detailData,
      packages,
      packagesLoading,
    } = useNewOrderModalData();
    const { getOrderDetailFields, getOrderTypeDetail } = useActions();
    const [modal, contextHolder] = useModal();

    // Set step description when an order type is already selected (e.g., on initial load or when navigating back)
    useEffect(() => {
      if (orderTypeId) {
        if (orderFlowType === ORDER_FLOW_TYPES.PACKAGE && selectedPackage) {
          const selectedOrderType = selectedPackage.order_types?.find(
            ot => ot.id === orderTypeId
          );
          if (selectedOrderType) {
            setStepDescription(0, selectedOrderType.name);
          }
        } else if (orderFlowType === ORDER_FLOW_TYPES.SIMPLE) {
          const orderType = find(orderTypes, { value: orderTypeId });
          if (orderType) {
            setStepDescription(0, orderType.label);
          }
        }
      }
    }, [
      orderTypeId,
      orderFlowType,
      selectedPackage,
      orderTypes,
      setStepDescription,
    ]);

    const confirmChangeOrderType = () => {
      modal.confirm({
        title: 'Change Order Type?',
        content: (
          <div>
            Are you sure you want to change the order type? Saved inputs will be
            discarded.
          </div>
        ),
        width: 423,
        centered: true,
        icon: <WarningOutlined />,
        okText: 'Yes, Change',
        cancelText: 'No, Cancel',
        onOk: () => {
          setAddItems({});
          return Promise.resolve();
        },
        onCancel: () => {
          form.setFieldsValue({ order_type_id: orderTypeId });
          return Promise.resolve();
        },
      });
    };

    useImperativeHandle(ref, () => ({
      validateStep(cb) {
        if (orderFlowType === ORDER_FLOW_TYPES.PACKAGE) {
          const values = form.getFieldsValue();
          const fieldErrors = validateForm(packageValidateObj, values);
          if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
            scrollToErrorField();
            return;
          }
          const chosenPkg = find(packages, { code: values.package_id });
          if (
            !chosenPkg ||
            !chosenPkg.order_types ||
            chosenPkg.order_types.length === 0
          ) {
            return;
          }
          const isSamePkgAsCurrent =
            selectedPackage && selectedPackage.code === chosenPkg.code;
          const requestedIndex = isSamePkgAsCurrent ? packageOrderIndex : 0;
          const safeIndex =
            requestedIndex >= 0 && requestedIndex < chosenPkg.order_types.length
              ? requestedIndex
              : 0;
          const cartSnapshot = cartOrders[safeIndex] || null;
          const snapshotOrderTypeId =
            cartSnapshot?.formValues?.order_type_id ||
            cartSnapshot?.orderTypeDetail?.id ||
            null;
          const targetOrderType =
            chosenPkg.order_types.find(ot => ot.id === snapshotOrderTypeId) ||
            chosenPkg.order_types[safeIndex];
          getOrderDetailFields(
            detailData.id,
            targetOrderType.id,
            fieldResponse => {
              if (!fieldResponse) return;
              getOrderTypeDetail(targetOrderType.id, detailResponse => {
                if (!detailResponse) return;
                setSelectedPackage(chosenPkg);
                setPackageOrderIndex(safeIndex);
                setOrderTypeId(targetOrderType.id);
                setFormDetailsFieldPopulated(false);
                setAddItems({});
                form.setFieldsValue({ order_type_id: targetOrderType.id });
                setStepDescription(0, targetOrderType.name);
                cb(true);
              });
            }
          );
          return;
        }
        const values = form.getFieldsValue();
        const fieldErrors = validateForm(orderTypeValidateObj, values);
        if (fieldErrors.length > 0) {
          form.setFields(fieldErrors);
          scrollToErrorField();
        } else {
          if (orderTypeId !== values.order_type_id) {
            getOrderTypeDetail(values.order_type_id, response => {
              if (!response) return;
              const nextCb = () => {
                setOrderTypeId(values.order_type_id);
                setFormDetailsFieldPopulated(false);
                const orderType = find(orderTypes, {
                  value: values.order_type_id,
                });
                setStepDescription(0, orderType.label);
                cb(true);
              };
              if (!isEditOrder || orderTypeId !== values.order_type_id) {
                getOrderDetailFields(
                  detailData.id,
                  values.order_type_id,
                  res => {
                    if (!res) return;
                    nextCb();
                  }
                );
              } else {
                nextCb();
              }
            });
          } else {
            cb(true);
          }
        }
      },
    }));

    const packageOptions = (packages || []).map(pkg => ({
      value: pkg.code,
      label: pkg.name,
    }));

    return (
      <div style={style} className={cn(classes.stepContent)}>
        {!isEditOrder && cartOrders.length === 0 && (
          <div className={styles.flowTypeSection}>
            <Checkbox
              checked={orderFlowType === ORDER_FLOW_TYPES.PACKAGE}
              disabled={isEditOrder && !!selectedPackage}
              onChange={e =>
                setOrderFlowType(
                  e.target.checked
                    ? ORDER_FLOW_TYPES.PACKAGE
                    : ORDER_FLOW_TYPES.SIMPLE
                )
              }
            >
              Package Order
            </Checkbox>
          </div>
        )}

        {/* Simple Order */}
        {orderFlowType === ORDER_FLOW_TYPES.SIMPLE && (
          <>
            <p className={styles.sectionHint}>
              Select the order type you&apos;d like to create.
            </p>
            <div className={styles.orderTypeSection}>
              <div>
                <FormSection>
                  <div className="selectField75">
                    <InputItem
                      name={'order_type_id'}
                      label="Order Type"
                      form={form}
                      type="select"
                      options={orderTypes}
                      required
                      onChange={() => {
                        const values = form.getFieldsValue();
                        if (
                          orderTypeId &&
                          orderTypeId !== values.order_type_id
                        ) {
                          confirmChangeOrderType();
                        } else {
                          setAddItems({});
                        }
                      }}
                    />
                  </div>
                </FormSection>
                <FormSection>
                  <InputItem
                    name={'requested_by'}
                    label="Order Requested By"
                    form={form}
                    type="select"
                    options={orderRequesters}
                    required
                  />
                </FormSection>
              </div>
              <div>
                <FormItem shouldUpdate>
                  {() => {
                    const values = form.getFieldsValue();
                    const foundOrderType =
                      find(orderTypes, { value: values.order_type_id }) || {};
                    return (
                      <span className={styles.description}>
                        {!!values.order_type_id &&
                          (foundOrderType.description || '')}
                      </span>
                    );
                  }}
                </FormItem>
              </div>
            </div>
          </>
        )}

        {/* Package Order */}
        {orderFlowType === ORDER_FLOW_TYPES.PACKAGE && (
          <div className={styles.packageSection}>
            <p className={styles.sectionHint}>
              Select a package bundle to create multiple orders at once.
            </p>
            <div className={styles.packageFormRow}>
              <FormSection>
                <div className="selectField75">
                  <InputItem
                    name={'package_id'}
                    label="Package"
                    form={form}
                    type="select"
                    options={packageOptions}
                    required
                    loading={packagesLoading}
                    disabled={isEditOrder && !!selectedPackage}
                    onChange={() => {}}
                  />
                </div>
              </FormSection>
              <FormSection>
                <InputItem
                  name={'requested_by'}
                  label="Order Requested By"
                  form={form}
                  type="select"
                  options={orderRequesters}
                  required
                />
              </FormSection>
            </div>

            <FormItem shouldUpdate>
              {() => {
                const values = form.getFieldsValue();
                const previewed = find(packages, { code: values.package_id });
                if (!previewed || !previewed.order_types?.length) return null;
                return (
                  <div className={styles.packagePreview}>
                    <p className={styles.packagePreviewTitle}>
                      This package includes {previewed.order_types.length} order
                      {previewed.order_types.length !== 1 ? 's' : ''}:
                    </p>
                    <div className={styles.packagePreviewList}>
                      {previewed.order_types.map((ot, idx) => (
                        <div key={ot.id} className={styles.packagePreviewItem}>
                          <span className={styles.packagePreviewIndex}>
                            {idx + 1}.
                          </span>
                          <span className={styles.packagePreviewName}>
                            {ot.name}
                          </span>
                          {ot.price != null && (
                            <span className={styles.packagePreviewPrice}>
                              {getCurrencyValue(ot.price)}
                            </span>
                          )}
                          {ot.description && (
                            <p className={styles.packagePreviewDesc}>
                              {ot.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }}
            </FormItem>
          </div>
        )}

        {contextHolder}
      </div>
    );
  }
);

export default OrderTypeStep;
